import { createContext, ReactNode, useMemo, useState } from 'react';
import { MobileAppBridge } from 'shared/data/MobileAppBridge';
import { USA_STATES } from 'shared/form/USAState';
import { useNullableContext } from 'shared/hooks/useNullableContext';
import { useOnboardingDialogState } from 'shared/modules/onboarding/core/OnboardingWrapper';
import { useCarrierSettings } from 'shared/settings/CarrierSettingsAPI';
import { IntrastateCarriersStateSelectionDialog } from '../../../../../loadboard/core/IntrastateCarriersStateSelectionDialog';
import { PostingDTO } from '../../../../../loadboard/data/dto/PostingDTO';
import { useLoadboardAccess } from './LoadboardAccessAPI';
import { LoadboardAccessDialog } from './LoadboardAccessDialog';
import { LoadboardNonVerifiableDialog } from './LoadboardNonVerifiableDialog';
import { LoadboardReviewedDialog } from './LoadboardReviewedDialog';

interface LoadboardAccessContextType {
  openAccessDialog: (callback?: () => void) => void;
  openStateSelectionDialog: () => void;
  shouldOpenStateSelector: (posting: PostingDTO) => boolean;
}

const Context = createContext<null | LoadboardAccessContextType>(null);

export function useLoadboardAccessContext() {
  return useNullableContext('LoadboardAccessContext', Context);
}

export function LoadboardAccessContext({ children }: { children: ReactNode }) {
  const [isDialogOpen, setDialogState] = useState(false);
  const [isStateSelectionDialogOpen, setStateSelectionDialogOpen] =
    useState(false);
  const { data: settings } = useCarrierSettings();
  const { data: access } = useLoadboardAccess();
  const { openBecomeVerifiedDialog } = useOnboardingDialogState();

  const ctx = useMemo(
    (): LoadboardAccessContextType => ({
      openAccessDialog(callback?: () => void) {
        const shouldShowMobileAppModal =
          access?.verification_status !== 'reviewed' &&
          access?.verification_status !== 'non_verifiable';

        //preventing opening native mobile window,
        // because there is special modal for reviewed carriers
        // that exists only on web
        if (MobileAppBridge.isInjected() && shouldShowMobileAppModal) {
          MobileAppBridge.openVerifiedCarrierPromotionalModal();
        } else if (access?.verification_status === 'not_submitted') {
          callback?.();
          openBecomeVerifiedDialog();
        } else {
          callback?.();
          setDialogState(true);
        }
      },
      openStateSelectionDialog() {
        setStateSelectionDialogOpen(true);
      },
      shouldOpenStateSelector(posting: PostingDTO) {
        if (
          access?.verification_status !== 'approved' ||
          posting.carrier_load
        ) {
          return false;
        }

        const pickupState = posting.load.pickup.venue.state?.toLowerCase();
        const deliveryState = posting.load.delivery.venue.state?.toLowerCase();
        const isPickUpInUS = USA_STATES.some(
          (state) => state.abbreviation.toLowerCase() === pickupState,
        );

        return (
          settings?.carrier.operation_type === 'Intrastate' &&
          (pickupState !== deliveryState || !isPickUpInUS)
        );
      },
    }),
    [
      access,
      settings,
      setDialogState,
      openBecomeVerifiedDialog,
      setStateSelectionDialogOpen,
    ],
  );

  return (
    <Context.Provider value={ctx}>
      {children}

      {access?.verification_status === 'reviewed' ? (
        <LoadboardReviewedDialog
          open={isDialogOpen}
          onClose={() => {
            setDialogState(false);
          }}
        />
      ) : access?.verification_status === 'non_verifiable' ? (
        <LoadboardNonVerifiableDialog
          open={isDialogOpen}
          onClose={() => {
            setDialogState(false);
          }}
        />
      ) : (
        <LoadboardAccessDialog
          open={isDialogOpen}
          onClose={() => {
            setDialogState(false);
          }}
        />
      )}

      <IntrastateCarriersStateSelectionDialog
        open={isStateSelectionDialogOpen}
        onClose={() => {
          setStateSelectionDialogOpen(false);
        }}
      />
    </Context.Provider>
  );
}
